/**
 * useAlerts
 *
 * Loads active (non-dismissed) clinical alerts and subscribes to
 * Supabase Realtime to receive new ones in real time.
 *
 * The realtime subscription is set up AFTER the initial load completes
 * to avoid a race condition where a new alert arriving during load gets
 * overwritten by the setState from the initial fetch.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { ClinicalAlert } from '../types';
import { getActiveAlerts, markAllRead, dismissAlert, dismissAllAlerts } from '../services/alertService';
import { supabase, isSupabaseAvailable } from '../services/supabaseClient';

export function useAlerts(clinicianId: string | undefined) {
  const [alerts, setAlerts] = useState<ClinicalAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  const unreadCount = alerts.filter((a) => !a.readAt).length;
  const criticalCount = alerts.filter((a) => a.severity === 'critical' && !a.readAt).length;

  const reload = useCallback(async () => {
    if (!clinicianId) return;
    const data = await getActiveAlerts(clinicianId);
    setAlerts(data);
    setLoading(false);
    loadedRef.current = true;
  }, [clinicianId]);

  // Initial load + realtime subscription (sequential to avoid race condition)
  useEffect(() => {
    if (!clinicianId) { setLoading(false); return; }

    loadedRef.current = false;
    let channel: ReturnType<NonNullable<typeof supabase>['channel']> | null = null;

    reload().then(() => {
      // Subscribe only after initial load completes so no in-flight alert is lost
      if (!isSupabaseAvailable() || !supabase) return;

      channel = supabase
        .channel(`alerts-${clinicianId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'clinical_alerts',
            filter: `clinician_id=eq.${clinicianId}`,
          },
          (payload) => {
            const row = payload.new as Record<string, unknown>;
            const newAlert: ClinicalAlert = {
              id:             row.id as string,
              clinicianId:    row.clinician_id as string,
              patientId:      row.patient_id as string | null,
              testResultId:   row.test_result_id as string,
              testCode:       row.test_code as string,
              patientName:    row.patient_name as string,
              severity:       row.severity as ClinicalAlert['severity'],
              message:        row.message as string,
              triggeredScore: row.triggered_score as number | null,
              triggeredAt:    row.triggered_at as string,
              readAt:         null,
              dismissedAt:    null,
            };

            setAlerts((prev) => {
              // Avoid duplicates in case the alert was already included in the initial load
              if (prev.some((a) => a.id === newAlert.id)) return prev;
              return [newAlert, ...prev];
            });

            if (newAlert.severity === 'critical') {
              toast.error(`🚨 Alerta crítica — ${newAlert.patientName}: ${newAlert.message}`, {
                duration: 8000,
              });
            } else {
              toast.warning(`⚠️ Alerta — ${newAlert.patientName}: ${newAlert.message}`, {
                duration: 5000,
              });
            }
          }
        )
        .subscribe();
    });

    return () => {
      if (channel && supabase) supabase.removeChannel(channel);
    };
  }, [clinicianId, reload]);

  const handleMarkAllRead = useCallback(async () => {
    if (!clinicianId) return;
    await markAllRead(clinicianId);
    setAlerts((prev) => prev.map((a) => ({ ...a, readAt: a.readAt ?? new Date().toISOString() })));
  }, [clinicianId]);

  const handleDismiss = useCallback(async (alertId: string) => {
    await dismissAlert(alertId);
    setAlerts((prev) => prev.filter((a) => a.id !== alertId));
  }, []);

  const handleDismissAll = useCallback(async () => {
    if (!clinicianId) return;
    await dismissAllAlerts(clinicianId);
    setAlerts([]);
  }, [clinicianId]);

  return {
    alerts,
    loading,
    unreadCount,
    criticalCount,
    reload,
    markAllRead: handleMarkAllRead,
    dismiss: handleDismiss,
    dismissAll: handleDismissAll,
  };
}
